import React, { useEffect, useState } from "react";
import {
  Card,
  Tag,
  Progress,
  Row,
  Col,
  Dropdown,
  Menu,
  Skeleton,
  Checkbox,
  Avatar,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  ClockCircleOutlined,
  HeartOutlined,
  MoreOutlined,
  CalendarOutlined,
  TeamOutlined,
  AimOutlined,
  EditOutlined,
  CopyOutlined,
  InboxOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { CustomSecondaryButton } from "../../physician_page/styled";
import journeyService from "../../../../services/journey";
import { useHistory } from "react-router-dom";
import {
  JourneyCardWrapper,
  CardHeader,
  HeaderLeft,
  CardTitle,
  CardDescription,
  ProgressContainer,
  ProgressHeader,
  StyledProgress,
  DateInfo,
  DateItem,
  DateSeparator,
  CardActions,
  PreviewButton,
  MenuIcon,
  EmptyStateCard,
  EmptyStateText,
  StatusTag,
} from "./Styled";
import CreateJourneyModal from "../modals/CreateJourneyModal";
import DeleteConfirmationModal from "../../physician_page/component/DeleteConfirmationModal";
import { notify } from "../../../../utils/helper";
import { StyledMenu } from "./TemplateCard";
import { FiUserPlus } from "react-icons/fi";
import AssignJourneyModal from "../modals/AssignJourneyModal";
import { capitalizeFirstLetter } from "../../../../utils/utils";

const CCMTag = styled(Tag)`
  background-color: rgba(138, 62, 244, 0.1);
  color: #8a3ef4;
  border: none;
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: var(--secondary-text-color);
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const BillingCycleTag = styled(Tag)`
  border-radius: 12px;
  padding: 2px 12px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid var(--border-color);
  background-color: var(--primary-bg);
  color: var(--primary-text-color);
  margin: 0;
`;

const MetricBox = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 12px;
  background-color: var(--primary-bg);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  height: 100%;
  background-color: ${(props) => props.color || "var(--primary-bg)"};
  color: ${(props) => (props.color ? "#fff" : "var(--primary-text-color)")};
`;

const MetricIcon = styled.div`
  color: var(--secondary-text-color);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const MetricValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${(props) => props.color || "var(--primary-text-color)"};
  line-height: 1.2;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${(props) => props.color || "var(--secondary-text-color)"};
  font-weight: 600;
`;

const BillableTag = styled(Tag)`
  background-color: #10b981;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  margin: 4px 0 0 0;
`;

const GoalsText = styled.div`
  font-size: 12px;
  color: #10b981;
  font-weight: 600;
  margin-top: 2px;
`;

const CareTeamText = styled.div`
  font-size: 12px;
  color: #d97706;
  font-weight: 600;
  margin-top: 2px;
`;

const LogTimeButton = styled(CustomSecondaryButton)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  padding: 8px 16px;
  border: 1px solid var(--border-color);
`;

const CCMPlanCard = ({
  selectedClinicalId,
  setSelectedClinicalId,
  fetchJourneys,
  journeys,
  loading,
  setJourneyName,
  journeyData,
  setJourneyData,
  currentPage,
  hasMore,
  loadMoreLoading,
  setIsAssignJourneyModalOpen,
}) => {
  const history = useHistory();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [ccmJourneyEdit, setCcmJourneyEdit] = useState(false);
  const [selectedJourneyId, setSelectedJourneyId] = useState([]);
  const [assignJourneyModal, setAssignJourneyModal] = useState(false);

  useEffect(() => {
    fetchJourneys("ccm", 1, false);
    setJourneyName("ccm");
  }, []);

  const handleLoadMore = () => {
    fetchJourneys("ccm", currentPage + 1, true);
  };

  // Helper functions for calculating derived values
  const steps = (journey) => journey?.steps || [];
  const uniqueSpecialties = (journey) =>
    Array.from(
      new Set(
        steps(journey)
          .map((s) => s.video?.specialty)
          .filter(Boolean)
      )
    );
  const totalVideos = (journey) => steps(journey).length;
  const videoProgress = (journey) =>
    steps(journey).filter((s) => s.video?.watched).length || 0;
  const conditions = (journey) => uniqueSpecialties(journey).length;
  const careTeamMembers = (journey) =>
    steps(journey).reduce((acc, s) => acc + (s.video?.patient?.length || 0), 0);
  const progressPercent = (journey) =>
    totalVideos(journey) > 0
      ? Math.round((videoProgress(journey) / totalVideos(journey)) * 100)
      : 0;

  const handlePreviewClick = (journey) => {
    const id = journey?.id || journey?._id;
    if (id) {
      history.push(`/pages/journeys/details/${id}`);
    }
  };

  const handleLogTime = (journey) => {
    // TODO: Implement log time functionality
  };

  const handleEditPlan = (journey) => {
    setSelectedJourney(journey);
    setIsEditModalOpen(true);
    setCcmJourneyEdit(true);
  };

  const handleDuplicate = (journey) => {
    // TODO: Implement duplicate functionality
  };

  const handleArchive = (journey) => {
    // TODO: Implement archive functionality
  };

  const handleDelete = (journey) => {
    setSelectedJourney(journey);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedJourney) return;
    try {
      setDeleteLoading(true);
      const id = selectedJourney?.id || selectedJourney?._id;
      await journeyService.deleteJourney({ ids: [id] });
      notify("success", "CCM plan deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedJourney(null);
      fetchJourneys("ccm");
    } catch (error) {
      notify("error", "Failed to delete CCM plan");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditSuccess = () => {
    fetchJourneys("ccm");
  };

  const handleAssignButton = (journey) => {
    if (journey) {
      setSelectedClinicalId(journey.id);
      setJourneyData(journey);
      setIsAssignJourneyModalOpen(true);
    } else {
      setSelectedClinicalId("");
      setJourneyData([]);
    }
  };

  const handleAssignJourney = (journeyId) => {
    setJourneyData(journeyId);
    setSelectedJourneyId(journeyId?.id);
    setAssignJourneyModal(true);
  };

  const handleSuccess = () => {
    fetchJourneys("ccm");
  };

  const handleStatusChange = async (status, journey) => {
    try {
      await journeyService.journeyStatusChange({ id: journey.id, status });
      fetchJourneys("ccm");
      notify("success", "Status updated successfully.");
    } catch (err) {
      notify("error", "Failed to update status.");
    }
  };

  const statusMenu = (journey) => (
    <StyledMenu onClick={({ key }) => handleStatusChange(key, journey)}>
      <Menu.Item key="active">
        <span className="menu-item-text">Active</span>
      </Menu.Item>
      <Menu.Item key="completed">
        <span className="menu-item-text">Completed</span>
      </Menu.Item>
      <Menu.Item key="draft">
        <span className="menu-item-text">Draft</span>
      </Menu.Item>
    </StyledMenu>
  );

  const menu = (journey) => (
    <StyledMenu>
      <Menu.Item key="edit" onClick={() => handleEditPlan(journey)}>
        <div className="menu-item-content">
          <EyeOutlined className="menu-item-icon" />
          <span className="menu-item-text">Edit Plan</span>
        </div>
      </Menu.Item>
      {/* {journey?.assignedPatients.length === 0 && ( */}
        <Menu.Item
          key="assign"
          onClick={() => handleAssignJourney(journey)}
        >
          <div className="menu-item-content">
            <FiUserPlus className="menu-item-icon" />
            <span className="menu-item-text">Assign Plan</span>
          </div>
        </Menu.Item>
      {/* )} */}
      {/* <Menu.Item key="duplicate" onClick={() => handleDuplicate(journey)}>
        <div className="menu-item-content">
          <CopyOutlined className="menu-item-icon" />
          <span className="menu-item-text">Duplicate</span>
        </div>
      </Menu.Item> */}
      {/* <Menu.Item key="archive" onClick={() => handleArchive(journey)}>
        <div className="menu-item-content">
          <InboxOutlined className="menu-item-icon" />
          <span className="menu-item-text">Archive</span>
        </div>
      </Menu.Item> */}
      <Menu.Divider />
      <Menu.Item key="delete" onClick={() => handleDelete(journey)}>
        <div className="menu-item-content">
          <DeleteOutlined className="menu-danger-icon" />
          <span className="menu-danger-text">Delete</span>
        </div>
      </Menu.Item>
    </StyledMenu>
  );

  if (loading) {
    return (
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        {[1, 2, 3].map((idx) => (
          <Col span={8} key={idx}>
            <JourneyCardWrapper>
              <CardHeader>
                <HeaderLeft>
                  <Skeleton.Button
                    active
                    size="small"
                    style={{ width: 80, height: 24 }}
                  />
                </HeaderLeft>
                <Skeleton.Button
                  active
                  size="small"
                  style={{ width: 24, height: 24 }}
                />
              </CardHeader>
              <Skeleton
                active
                paragraph={{ rows: 2 }}
                style={{ marginBottom: 16 }}
              />

              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Col xs={12} sm={12} lg={6} key={i}>
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </Col>
                ))}
              </Row>
              <Skeleton
                active
                paragraph={{ rows: 1 }}
                style={{ marginBottom: 16 }}
              />
              <CardActions>
                <Skeleton.Button
                  active
                  size="default"
                  style={{ flex: 1, height: 36 }}
                />
                <Skeleton.Button
                  active
                  size="default"
                  style={{ flex: 1, height: 36 }}
                />
              </CardActions>
            </JourneyCardWrapper>
          </Col>
        ))}
      </Row>
    );
  }

  if (journeys?.length === 0) {
    return (
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col span={24}>
          <EmptyStateCard>
            <EmptyStateText>No CCM plans available</EmptyStateText>
          </EmptyStateCard>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {isEditModalOpen && (
        <CreateJourneyModal
          isOpen={isEditModalOpen}
          setOpen={setIsEditModalOpen}
          isEdit={ccmJourneyEdit}
          setIsEdit={setCcmJourneyEdit}
          journeyId={selectedJourney}
          onSuccess={handleEditSuccess}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onConfiem={handleConfirmDelete}
          loading={deleteLoading}
          title="Delete CCM Plan"
          message="Are you sure you want to delete this CCM plan? This action cannot be undone."
        />
      )}
      {assignJourneyModal && (
        <AssignJourneyModal
          isOpen={assignJourneyModal}
          setOpen={setAssignJourneyModal}
          journeyId={selectedJourneyId}
          onSuccess={handleSuccess}
          selectedClinicalId={selectedClinicalId}
          setSelectedClinicalId={setSelectedClinicalId}
          journeyData={journeyData}
        />
      )}
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        {journeys?.map((journey, idx) => {
          const journeyCompleted = journey?.assignedPatients[0];
          const totalSteps = journey?.steps?.length;
          const progress = journey?.progress || 0;
          return (
            <Col
              lg={8}
              md={12}
              sm={24}
              xs={24}
              key={journey?.id || journey?._id || idx}
            >
              <JourneyCardWrapper key={journey?.id || journey?._id || idx}>
                <CardHeader>
                  <HeaderLeft>
                    {/* {journey?.assignedPatients.length === 0 && ( */}
                      {/* <Checkbox
                        checked={selectedClinicalId.includes(journey.id)}
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, journey)
                        }
                      /> */}
                    {/* )} */}
                    <CCMTag>
                      <HeartOutlined style={{ color: "#8a3ef4" }} />
                      CCM Plan
                    </CCMTag>
                  </HeaderLeft>
                  <Dropdown overlay={menu(journey)} trigger={["click"]} getPopupContainer={triggerNode => triggerNode.parentNode}>
                    <MenuIcon />
                  </Dropdown>
                </CardHeader>

                <CardTitle>{journey?.name || "Untitled Journey"}</CardTitle>
                <CardDescription>
                  {journey?.description || "No description available"}
                </CardDescription>

                <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12}>
                    <InfoLabel>Patient</InfoLabel>
                    {(() => {
                      const list = journey?.assignedPatients || [];
                    
                      if (list.length === 0) return <InfoValue>N/A</InfoValue>;
                      if (list.length === 1) return <InfoValue>{list[0]?.patientId?.name}</InfoValue>;
                    
                      return (
                        <Avatar.Group 
                          maxCount={2} 
                          size="small"
                        >
                          {list.map((item) => (
                            <Tooltip
                              title={capitalizeFirstLetter(item?.patientId?.name)}
                              key={item?.patientId?.id}
                            >
                            <Avatar key={item?.patientId?.id}>
                              {item?.patientId?.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            </Tooltip>
                          ))}
                        </Avatar.Group>
                      );
                    })()}
                  </Col>
                  <Col xs={24} sm={12}>
                    <InfoLabel>Coordinator</InfoLabel>
                    {journey?.coordinatorId ? (
                      <InfoValue>{`Dr. ${journey?.coordinatorId?.name}`}</InfoValue>
                    ) : (
                      "N/A"
                    )}
                  </Col>
                </Row>

                <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12}>
                    <InfoLabel>Billing Cycle</InfoLabel>
                    <BillingCycleTag>
                      {capitalizeFirstLetter(journey?.billingCycle) || "Monthly"}
                    </BillingCycleTag>
                  </Col>
                  <Col xs={24} sm={12}>
                    <InfoLabel>Status</InfoLabel>
                    <InfoValue style={{ display: "flex", alignItems: "center" }}>
                      <StatusTag className={journey?.status?.toLowerCase()}>
                        {journey?.status || "active"}
                      </StatusTag>
                      <Dropdown overlay={statusMenu(journey)} trigger={["click"]} getPopupContainer={triggerNode => triggerNode.parentNode}>
                        <EditOutlined
                          style={{
                            cursor: "pointer",
                            color: "var(--secondary-text-color)",
                            marginLeft: 8,
                          }}
                        />
                      </Dropdown>
                    </InfoValue>
                  </Col>
                </Row>

                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={24} md={12} lg={8}>
                    <MetricBox
                      color="#eff6ff"
                      style={{ border: "1px solid #dbeafe" }}
                    >
                      <MetricIcon>
                        <ClockCircleOutlined
                          style={{ color: "#1d4ed8" }}
                          size={14}
                        />
                        <MetricLabel color="#1d4ed8">Minutes</MetricLabel>
                      </MetricIcon>
                      <MetricValue color="#1e3a8a">
                        {journey?.totalWatchTime || 0}/{journey?.minutes || 0}
                      </MetricValue>
                      <BillableTag>{journey?.totalWatchTime > journey?.minutes ? "Billable" : "In Progress"}</BillableTag>
                    </MetricBox>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={8}>
                    <MetricBox
                      color="#faf5ff"
                      style={{ border: "1px solid #f3e8ff" }}
                    >
                      <MetricIcon>
                        <HeartOutlined style={{ color: "#7e22ce" }} size={14} />
                        <MetricLabel color="#7e22ce">Conditions</MetricLabel>
                      </MetricIcon>
                      <MetricValue color="#581c87">
                        {journey?.totalTags || 0}
                      </MetricValue>
                      <MetricLabel color="#9333ea">
                        Chronic diagnoses
                      </MetricLabel>
                    </MetricBox>
                  </Col>
                  {/* <Col xs={12} sm={12} lg={6}>
                <MetricBox color="#F0FDF4">
                  <MetricIcon>
                    <AimOutlined size={14} />
                    <MetricLabel>Goals</MetricLabel>
                  </MetricIcon>
                  <MetricValue>
                    {totalVideos(journey)}/{totalVideos(journey)}
                  </MetricValue>
                  {totalVideos(journey) > 0 && <GoalsText>Achieved</GoalsText>}
                </MetricBox>
              </Col> */}
                  <Col xs={24} sm={24} md={12} lg={8}>
                    <MetricBox
                      color="#fffbeb"
                      style={{ border: "1px solid #fef3c7" }}
                    >
                      <MetricIcon>
                        <TeamOutlined style={{ color: "#b45309" }} size={14} />
                        <MetricLabel color="#b45309">Teams</MetricLabel>
                      </MetricIcon>
                      <MetricValue color="#78350f">
                        {journey?.team}
                      </MetricValue>
                      <CareTeamText>Members</CareTeamText>
                    </MetricBox>
                  </Col>
                </Row>

                <ProgressContainer>
                  <ProgressHeader>
                    <span>Video Progress</span>
                    <span className="font-semibold">
                      Completed
                      ({progress || 0}%)
                    </span>
                  </ProgressHeader>
                  <StyledProgress
                    percent={progress}
                    showInfo={false}
                    strokeColor="#8a3ef4"
                  />
                </ProgressContainer>

                <DateInfo>
                  <DateItem>
                    <CalendarOutlined
                      style={{
                        fontSize: "14px",
                        color: "var(--secondary-text-color)",
                      }}
                    />
                    <span>
                      Enrolled{" "}
                      {journey?.createdAt
                        ? new Date(journey.createdAt).toLocaleDateString()
                        : ""}
                    </span>
                  </DateItem>
                  <DateSeparator>•</DateSeparator>
                  <DateItem>
                    <span>
                      Last contact{" "}
                      {journey?.updatedAt
                        ? new Date(journey.updatedAt).toLocaleDateString()
                        : ""}
                    </span>
                  </DateItem>
                </DateInfo>

                <CardActions>
                  <PreviewButton onClick={() => handlePreviewClick(journey)}>
                    <EyeOutlined />
                    Preview
                  </PreviewButton>
                  <PreviewButton onClick={() => handleAssignButton(journey)}>
                    <FiUserPlus />
                    Assign Plan
                  </PreviewButton>
                </CardActions>
              </JourneyCardWrapper>
            </Col>
          );
        })}
      </Row>
      {hasMore && (
        <Row className="mt-25 w-100 d-flex justify-content-center" style={{ padding: '20px 0' }}>
          <CustomSecondaryButton onClick={handleLoadMore} loading={loadMoreLoading}>
            Load More Plans
          </CustomSecondaryButton>
        </Row>
      )}
      {!hasMore && journeys.length > 0 && (
        <Row className="mt-25 w-100 d-flex justify-content-center" style={{ padding: '20px 0' }}>
          <p style={{ color: '#666', fontStyle: 'italic' }}>No more plans to load</p>
        </Row>
      )}
    </>
  );
};

export default CCMPlanCard;
